import React, { useContext, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom-v5-compat';
import classnames from 'classnames';

import { Input } from 'ui-components/components/input';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { ReportingCategoryDropdown } from './reporting-category-dropdown';
import { OrderTagsDropdown } from './order-tags-dropdown';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { Checkbox } from 'ui-components/components/checkbox';

import { UpsellModal } from '../../../../components/upsell-modal/upsell-modal';
import { useEnableBundledProducts } from '../../../../feature-flags/hooks/use-enable-bundled-products';
import { useModal } from '../../../../hooks/use-modal';
import { ProductBundles } from '../../../../constants/features';
import { UpsellBanner } from './bundles/bundle-component-table/upsell-banner';
import { ProductVariants } from './product-variants';
import { ProductDetailsContext } from '../../context';
import { BundleContext } from '../../bundle-context';
import { SaveInPlace } from './save-in-place';
import { ProductTypes } from '../../../../enums/product-types';
import { ProductDetailsBundle } from './bundles';
import { InheritedTags } from './inherited-tags';
import { ShipStationFulfillmentSettings } from './shipstation-fulfillment-settings';
import { useIsSellerOnFeatureGatedPlan } from '../../../../feature-gates/use-is-seller-on-feature-gated-plan';
import { getSubscriptionPath } from '../../../../routes/constants';

import { messages } from '../../messages';
import { messages as generalMessages } from './messages';
import style from '../../style.module.less';
import generalStyle from './style.module.less';

export const General = () => {
  const navigate = useNavigate();
  const {
    userHasPermission,
    description,
    setDescription,
    imageUrl,
    setImageUrl,
    upc,
    setUpc,
    isReturnable,
    setIsReturnable,
    productType,
    productId,
    isSaveInPlaceRequired,
    parentProduct,
    isShipStationFulfillmentEnabled,
    closeModal,
  } = useContext(ProductDetailsContext);
  const { isBundle, setIsBundle } = useContext(BundleContext);
  const isNew = !productId;
  const showVariants = productType === ProductTypes.Parent.name && !isNew;
  const showInheritedTags = productType === ProductTypes.Variant.name;
  const { open, isOpen: isBundlesModalOpen, close } = useModal();

  const hasBundlesEnabled = useEnableBundledProducts();
  const isOnGoodBetterBestPlanProfile = useIsSellerOnFeatureGatedPlan();

  const handleUpsellBannerClick = useCallback(() => {
    if (isOnGoodBetterBestPlanProfile) {
      closeModal?.();
      navigate(getSubscriptionPath());
    } else {
      open();
    }
  }, [isOnGoodBetterBestPlanProfile, closeModal, navigate, open]);

  const renderBundleConfig = useCallback(() => {
    const subheaderMessage = isOnGoodBetterBestPlanProfile
      ? generalMessages.upgradeToStandardOrPremiumDescription
      : generalMessages.upsellDescription;

    return (
      <>
        {!hasBundlesEnabled && (
          <>
            <UpsellBanner buttonMessage={generalMessages.upgrade} onOpen={handleUpsellBannerClick} subheaderMessage={subheaderMessage} />
            {isBundlesModalOpen && <UpsellModal onClose={close} upsellModalContentCode={ProductBundles} withContext />}
          </>
        )}
        <div
          className={classnames(style.isBundleToggle, {
            [style.hasBundlesDisabled]: !hasBundlesEnabled,
          })}
        >
          <div className={generalStyle.bundleLabel}>
            <FormattedMessage {...messages.isBundle} />
          </div>
          <CheckboxSlider
            checkboxId={`${productId}-bundle`}
            checked={isBundle}
            disabled={!userHasPermission || !hasBundlesEnabled}
            labelText={<FormattedMessage {...messages.productIsBundleLabel} />}
            onChange={setIsBundle}
          />
        </div>
      </>
    );
  }, [
    hasBundlesEnabled,
    handleUpsellBannerClick,
    isBundlesModalOpen,
    close,
    productId,
    isBundle,
    userHasPermission,
    setIsBundle,
    isOnGoodBetterBestPlanProfile,
  ]);

  return (
    <>
      <div className={classnames(style['shipping-defaults-header-wrapper'], style.dropped)}>
        <div className={style['shipping-defaults-header-text']}>
          <FormattedMessage {...messages.generalTitle} />
        </div>
      </div>
      <div className={generalStyle['general-options']}>
        <div className={generalStyle.option}>
          <div>
            <div className={generalStyle.label}>
              <FormattedMessage {...messages.description} />
            </div>
            <FloatTextArea data-testid='description' disabled={!userHasPermission} onChange={setDescription} value={description} />
          </div>
          <div className={generalStyle.label}>
            <FormattedMessage {...messages.imageUrl} />
          </div>
          <Input data-testid='thumbnail-url' disabled={!userHasPermission} onChange={setImageUrl} value={imageUrl} />
          <div className={generalStyle.description}>
            <FormattedMessage {...messages.imageUrlDescription} />
          </div>
        </div>
        <div className={generalStyle.option}>
          <div>
            <div className={generalStyle.label}>
              <FormattedMessage {...messages.productCategories} />
            </div>
            <ReportingCategoryDropdown />
          </div>
          <div>
            <div className={classnames(generalStyle.label, generalStyle['tags-label'])}>
              <FormattedMessage {...messages.orderTags} />
            </div>
            <OrderTagsDropdown />
          </div>
          {showInheritedTags && (
            <div>
              <div className={generalStyle.label}>
                <FormattedMessage {...messages.inheritedTags} />
              </div>
              <InheritedTags tagIds={parentProduct?.tags || []} />
            </div>
          )}
          <div className={generalStyle.upc}>
            <div className={generalStyle.label}>
              <FormattedMessage {...messages.upc} />
            </div>
            <Input data-testid='upc' disabled={!userHasPermission} onChange={setUpc} value={upc} />
          </div>
        </div>
        <div>
          <div className={generalStyle['is-returnable']}>
            <div className={generalStyle.label}>
              <FormattedMessage {...messages.isReturnable} />
            </div>
            <Checkbox
              checkboxId={`${productId}-is-returnable`}
              checked={isReturnable}
              disabled={!userHasPermission}
              label={<FormattedMessage {...messages.returnableMessage} />}
              onChange={setIsReturnable}
            />
          </div>
          {renderBundleConfig()}
        </div>
      </div>
      {hasBundlesEnabled && isBundle && <ProductDetailsBundle />}
      {isSaveInPlaceRequired && <SaveInPlace />}
      {showVariants && <ProductVariants />}
      {isShipStationFulfillmentEnabled && <ShipStationFulfillmentSettings />}
    </>
  );
};
